import { createApp, computed } from "vue";

import App from "./App.vue";
import router from "./router";
import store from "./store";
import { darkModeKey } from "@/config.js";
import { applyBrandColor, brandColorKey, loadBrandColor } from "@/brand-color.js";
import setupInterceptors from "./services/setupInterceptors";
import { notify } from "@/composables/useToast";
import { describeError } from "@/utils/error-message";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faTelegram } from "@fortawesome/free-brands-svg-icons";
import { faEnvelope, faQuestion } from "@fortawesome/free-solid-svg-icons";
import contenteditable from "vue-contenteditable";
import i18n from "@/globals/i18n";
import vueCountryRegionSelect from 'vue3-country-region-select'
import Vue3EasyDataTable from 'vue3-easy-data-table';

library.add([faTelegram, faEnvelope, faQuestion]);

import "./css/main.css";

/* Fetch sample data */
// store.dispatch("fetch", "clients");
// store.dispatch("fetch", "history");

/* Dark mode */
const localStorageDarkModeValue = localStorage.getItem(darkModeKey);

const isLoggedIn = computed(() => store.state.auth.status.loggedIn);

const userName = computed(() => isLoggedIn && store?.state?.auth?.user?.user?.name);

/* index.html applies this same rule to <html> before the first paint, so the
   page does not flash light while the bundle loads. This stays the authority:
   the store action adds the class from its own state (so an early `.dark` is
   harmless), and the `else` takes it back off if the two ever disagree. */
if (
  (localStorageDarkModeValue === null &&
    window.matchMedia("(prefers-color-scheme: dark)").matches) ||
  localStorageDarkModeValue === "1"
) {
  store.dispatch("darkMode");
} else {
  document.documentElement.classList.remove("dark");
}

/* Brand colour. Every `*-brand-*` class reads the variables this writes, so it
   has to run before the first render. The storage listener keeps other open
   tabs in step when the colour is changed in one of them. */
applyBrandColor(loadBrandColor());
window.addEventListener("storage", (event) => {
  if (event.key === brandColorKey) applyBrandColor(loadBrandColor());
});

/* Default title tag */
const defaultDocumentTitle = `Toplistbot.com User Panel`;

/* Collapse mobile aside menu on route change */
router.beforeEach((to) => {
  store.dispatch("asideMobileToggle", false);
  store.dispatch("asideLgToggle", false);

  /* Full screen mode */
  store.dispatch("fullScreenToggle", !!to.meta.fullScreen);
});

setupInterceptors(store);

router.afterEach((to) => {
  /* Set document title from route meta */
  if (to.meta && to.meta.title && isLoggedIn.value) {
    document.title = `${userName.value} ${to.meta.title} — ${defaultDocumentTitle}`;
  } else {
    document.title = `${defaultDocumentTitle}`;
  }

  /* Full screen mode */
  store.dispatch("fullScreenToggle", !!to.meta.fullScreen);
});

// Global card management utilities (accessible from browser console)
window.cardManager = {
  expandAllCards() {
    let count = 0;
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith('card-') && key.endsWith('-collapsed')) {
        localStorage.setItem(key, 'false');
        count++;
      }
    }
    console.log(`Expanded ${count} cards. Reloading page...`);
    window.location.reload();
  },
  
  resetAllCards() {
    let count = 0;
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith('card-') && key.endsWith('-collapsed')) {
        localStorage.removeItem(key);
        count++;
      }
    }
    console.log(`Reset ${count} card states. Reloading page...`);
    window.location.reload();
  },
  
  listCollapsedCards() {
    const collapsed = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('card-') && key.endsWith('-collapsed')) {
        const value = localStorage.getItem(key);
        const cardId = key.slice(5, -10); // Remove 'card-' and '-collapsed'
        collapsed.push({ cardId, isCollapsed: value === 'true' });
      }
    }
    console.table(collapsed);
    return collapsed;
  },
  
  help() {
    console.log(`
Card Manager Help:
- cardManager.expandAllCards() - Expand all collapsed cards
- cardManager.resetAllCards() - Remove all card collapse states
- cardManager.listCollapsedCards() - Show all card states
- cardManager.help() - Show this help message

If cards are stuck collapsed, use expandAllCards() or resetAllCards().
    `);
  }
};

// Log that card manager is available
console.log('Card Manager available! Type cardManager.help() for commands.');

const app = createApp(App);
app.use(store);
/* Registered before mount so the first render already resolves keys. The
   document's lang/dir are NOT set here: google-translate.js owns those and
   applies them from the same cookie this locale was seeded from. */
app.use(i18n);
app.use(router);
app.use(vueCountryRegionSelect);
app.use(Vue3EasyDataTable);
app.use(contenteditable);
app.component("font-awesome-icon", FontAwesomeIcon);

/* ── Last-resort error reporting ──────────────────────────────────────────
 *
 * Without this, an error thrown in render, in a lifecycle hook, or in a promise
 * nobody caught leaves the user staring at a half-drawn page with no
 * explanation. Individual screens still handle their own failures — this only
 * catches what slipped through.
 */

// A render loop can throw many times a second; one message is help, twenty is
// noise that buries the page.
const GLOBAL_ERROR_COOLDOWN_MS = 8000;
let lastGlobalErrorAt = 0;

// An axios rejection carries a message written for humans by the API. A
// TypeError carries "Cannot read properties of undefined (reading 'id')", which
// tells the user nothing and looks like the app is broken in a scary way — so
// only API failures get their real message.
const isApiError = (error) =>
  !!error && (error.isAxiosError === true || !!error.response || !!error.request);

/**
 * True for failures that happened outside our own code and that the user can do
 * nothing about.
 *
 * The case that prompted this: an ad blocker or privacy extension wraps
 * `window.fetch`, blocks the Google Translate widget's request, and the
 * resulting rejection reaches this handler. Nothing in the app is broken — only
 * the translate dropdown — but the user was shown "Something went wrong on this
 * page. Reloading usually fixes it", which is untrue and alarming. Reloading
 * cannot help; the extension will block it again.
 *
 * Two signals, both required, so a real network failure of OUR api still warns:
 *
 *  - `TypeError: Failed to fetch` is a network-LAYER failure (blocked, CORS,
 *    offline). A failing API call comes through axios with `response`/`request`
 *    set and is classified as an API error long before this runs.
 *  - The stack has no frame from this origin. A genuine fetch of ours would
 *    name one of our own chunks; a blocked third-party script names only the
 *    extension and the vendor bundle.
 */
const isThirdPartyNoise = (error) => {
  if (!(error instanceof TypeError)) return false;
  if (!/failed to fetch|networkerror|load failed/i.test(error.message || "")) return false;

  // No stack at all: cannot attribute it, so treat it as ours and warn.
  const stack = error.stack || "";
  if (!stack) return false;

  return !stack.includes(window.location.origin);
};

const reportUnexpectedError = (error, source) => {
  // Always log in full: the console is for us, the toast is for the user.
  console.error(`[${source}]`, error);

  // Logged above, but never surfaced — the user has nothing to act on.
  if (isThirdPartyNoise(error)) return;

  const now = Date.now();
  if (now - lastGlobalErrorAt < GLOBAL_ERROR_COOLDOWN_MS) return;
  lastGlobalErrorAt = now;

  try {
    if (isApiError(error)) {
      // Status-derived wording, plus the built-in action where there is one
      // (401 offers "Sign in again", 409 offers "Reload").
      const described = describeError(error);
      notify({
        severity: described.severity,
        title: described.title,
        detail: described.detail,
        action: described.action,
      });
      return;
    }

    // A thrown TypeError says nothing a user can use, so the only honest offer
    // is the one thing that actually tends to fix a broken render.
    notify({
      severity: "error",
      title: "Something went wrong on this page",
      detail: "Reloading usually fixes it. If it keeps happening, let us know.",
      action: { label: "Reload the page", onClick: () => window.location.reload(), keepOpen: true },
    });
  } catch (notifyError) {
    // The notification system itself failed; the console log above still stands.
    console.error("[error-reporter] could not display the message", notifyError);
  }
};

app.config.errorHandler = (error) => reportUnexpectedError(error, "vue");

window.addEventListener("unhandledrejection", (event) =>
  reportUnexpectedError(event.reason, "unhandled-rejection")
);

app.mount("#app");
